import { useState } from 'react';
import DemoBadge from '@/components/feature/DemoBadge';
import UploadPanel from './UploadPanel';
import AiPrepPanel from './AiPrepPanel';
import EditorWorkspace from './EditorWorkspace';
import ArticlePreview from './ArticlePreview';
import StageStepper from './StageStepper';
import PublishFlow from './PublishFlow';
import { EMPTY_FORM, SAMPLE_AI_OUTPUT } from './studioData';
import type { StudioForm } from './studioData';

/**
 * AI Story Studio — a fully client-side demonstration.
 *
 * Nothing here talks to the Backend or any AI model. The "AI" output is clearly
 * labelled sample content, and publishing is a labelled simulation only.
 */
export default function StoryStudio({ onExit }: { onExit: () => void }) {
  const [form, setForm] = useState<StudioForm>(EMPTY_FORM);
  const [pastedText, setPastedText] = useState('');
  const [fileName, setFileName] = useState('');
  const [stage, setStage] = useState(0);
  const [prepared, setPrepared] = useState(false);
  const [notice, setNotice] = useState('');
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

  const flash = (message: string) => setNotice(message);
  const canPrepare = Boolean(pastedText.trim() || fileName || form.body.trim());

  const handleFile = (name: string) => {
    setFileName(name);
    flash(`"${name}" selected. Document processing is available in the production version — its contents are not extracted here. Paste text below to populate the editor.`);
  };

  const handleUsePastedText = () => {
    setForm((prev) => ({ ...prev, body: pastedText }));
    flash('Pasted text added to the article body. Run "Prepare with AI" to see the suggested structure.');
  };

  const handlePrepare = () => {
    setForm(SAMPLE_AI_OUTPUT);
    setPrepared(true);
    setStage(1);
    flash('Sample AI output applied to the editor. No AI model is connected — these are clearly labelled example suggestions you can edit.');
  };

  const handleFieldChange = (key: keyof StudioForm, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (stage >= 1 && stage < 3) setStage(2);
  };

  const handleMarkReady = () => {
    setStage(3);
    flash('Marked ready to publish. In the live product this moves the story to the publish queue.');
  };

  const handleSimulatePublish = () => {
    setStage(4);
    flash('Simulation only — nothing was published or saved.');
  };

  const handleReset = () => {
    setForm(EMPTY_FORM);
    setPastedText('');
    setFileName('');
    setStage(0);
    setPrepared(false);
    setPreviewMode('desktop');
    flash('Studio reset. Start again by uploading or pasting text.');
  };

  return (
    <section>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button
          type="button"
          onClick={onExit}
          className="inline-flex items-center gap-2 border border-background-300 hover:border-foreground-700 text-foreground-700 font-label font-bold uppercase tracking-wider text-[11px] px-3.5 py-2 rounded-md whitespace-nowrap cursor-pointer transition-colors"
        >
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
          Back to library
        </button>
        <h2 className="font-heading font-extrabold uppercase text-2xl md:text-3xl text-foreground-950">AI Story Studio</h2>
        <DemoBadge label="Demo — nothing is saved or published" />
        <button
          type="button"
          onClick={handleReset}
          className="ml-auto inline-flex items-center gap-2 text-foreground-600 hover:text-foreground-900 font-label font-bold uppercase tracking-wider text-[11px] whitespace-nowrap cursor-pointer transition-colors"
        >
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-restart-line"></i></span>
          Reset demo
        </button>
      </div>

      <StageStepper stage={stage} />

      {notice && (
        <p className="mt-4 bg-secondary-50 border border-secondary-200 text-secondary-900 text-sm rounded-md px-4 py-3 flex items-start gap-2">
          <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"><i className="ri-information-line"></i></span>
          <span>{notice}</span>
        </p>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 mt-6">
        <div className="xl:col-span-5">
          <UploadPanel
            fileName={fileName}
            onFile={handleFile}
            pastedText={pastedText}
            onPastedTextChange={setPastedText}
            onUsePastedText={handleUsePastedText}
          />
        </div>
        <div className="xl:col-span-7">
          <AiPrepPanel prepared={prepared} canPrepare={canPrepare} onPrepare={handlePrepare} />
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 mt-6">
        <div className="xl:col-span-6">
          <EditorWorkspace form={form} onChange={handleFieldChange} />
        </div>
        <div className="xl:col-span-6">
          <ArticlePreview form={form} mode={previewMode} onModeChange={setPreviewMode} />
        </div>
      </div>

      <div className="mt-6">
        <PublishFlow stage={stage} onMarkReady={handleMarkReady} onSimulatePublish={handleSimulatePublish} />
      </div>
    </section>
  );
}